/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

/**
 * TanStack Query hooks for admin state configuration (Profile, Quality & Deadlines, File Types).
 * Server state from GET/PUT /admin/state-config/{state_code}.
 * State defaults for New Submission from GET /admin/state-config/current.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { authenticatedApi } from "@/api/authenticatedClient";
import { invalidateConfigDerived } from "@/api/cacheInvalidation";
import { BRANDING_QUERY_KEY } from "@/api/queryKeys";

/** Response from GET /admin/state-config/current — used by New Submission, Dashboard (deadline KPI), and SubmissionDetail. */
export interface StateConfigDefaults {
  state_code: string;
  state_name: string;
  tmsis_version: string;
  quality_score_target: number;
  quality_score_warning: number;
  quality_score_critical: number;
  submission_deadline_day: number;
  submission_deadline_type: string;
  submission_deadline_business_day: number | null;
}

/** Strict Zod schema for GET /admin/state-config/current response (parse at API boundary). */
const stateConfigDefaultsSchema = z.object({
  state_code: z.string(),
  state_name: z.string(),
  tmsis_version: z.string(),
  quality_score_target: z.number(),
  quality_score_warning: z.number(),
  quality_score_critical: z.number(),
  submission_deadline_day: z.number(),
  submission_deadline_type: z.string(),
  submission_deadline_business_day: z.number().nullable(),
});

const stateConfigDefaultsQueryKey = ["admin", "state-config", "current"] as const;

/** Consume a promise so it does not float (satisfies no-floating-promises). No-op if TanStack Query returns void/undefined. */
function ignorePromise(p: unknown): void {
  if (p instanceof Promise) {
    p.catch(() => undefined);
  }
}

async function fetchStateConfigDefaults(): Promise<StateConfigDefaults> {
  const res = await authenticatedApi.get<unknown>("/admin/state-config/current");
  const raw = res && typeof res === "object" && "data" in res ? res.data : res;
  return stateConfigDefaultsSchema.parse(raw);
}

/** Hook to fetch current state defaults (state_code, tmsis_version) for New Submission. Reflects State Configuration settings. */
export function useStateConfigDefaults() {
  return useQuery({
    queryKey: stateConfigDefaultsQueryKey,
    queryFn: fetchStateConfigDefaults,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: (failureCount, error) => {
      // Don't retry a 404 (resource not configured yet). Duck-type the status rather than
      // `instanceof ApiError` so this stays robust when a test mocks the API client without the
      // `ApiError` export (referencing it there throws an unhandled rejection inside the retryer).
      const status = (error as { status?: number } | null | undefined)?.status;
      if (status === 404) return false;
      return failureCount < 2;
    },
  });
}

/** Per-file-type item from API (snake_case). */
export interface StateConfigFileTypeItemApi {
  is_enabled: boolean;
  deadline_days: number;
  is_required: boolean;
  enabled_segments: Record<string, boolean> | null;
}

/** Contact assignment from API (snake_case). */
export interface StateConfigContactAssignmentApi {
  id: string;
  user_id: string;
  role_id: string;
  is_primary: boolean;
  notifications: string[];
  email_enabled: boolean;
  sms_enabled: boolean;
}

/** API response shape (snake_case from backend). */
export interface StateConfigApiResponse {
  state_code: string;
  state_name: string;
  cms_region: string | null;
  tmsis_version: string;
  reporting_frequency: "monthly" | "quarterly";
  fiscal_year_start_month: number;
  submission_window_days: number;
  submission_deadline_day: number;
  submission_deadline_type: "fixed" | "custom";
  submission_deadline_business_day: number | null;
  auto_lock_enabled: boolean;
  auto_lock_days: number;
  quality_score_target: number;
  quality_score_warning: number;
  quality_score_critical: number;
  file_types: Record<string, StateConfigFileTypeItemApi>;
  contact_assignments?: StateConfigContactAssignmentApi[];
  /** Set when non-admin save created a change request. */
  change_request_id?: string | null;
  request_number?: string | null;
  incident_automation?: {
    enabled: boolean;
    include_critical: boolean;
    include_high: boolean;
    oba_category_expenditure: boolean;
  };
  vvl_saved?: number | null;
  vvl_skipped?: number | null;
}

/** Single VVL record sent with state config save. */
export interface ValidValuesUploadRecordApi {
  vvl_order: number;
  vvl_field: string;
  vvl_name: string;
  vvl_code: string;
  vvl_code_description: string | null;
  effective_start_date: string | null;
  effective_end_date: string | null;
}

/** PUT request body (snake_case). */
export interface StateConfigUpdateApi {
  state_code?: string;
  state_name?: string;
  cms_region?: string | null;
  tmsis_version?: string;
  reporting_frequency?: "monthly" | "quarterly";
  fiscal_year_start_month?: number;
  submission_window_days?: number;
  submission_deadline_day?: number;
  submission_deadline_type?: "fixed" | "custom";
  submission_deadline_business_day?: number | null;
  auto_lock_enabled?: boolean;
  auto_lock_days?: number;
  quality_score_target?: number;
  quality_score_warning?: number;
  quality_score_critical?: number;
  file_types?: Record<string, StateConfigFileTypeItemApi>;
  contact_assignments?: StateConfigContactAssignmentApi[];
  incident_automation?: {
    enabled: boolean;
    include_critical: boolean;
    include_high: boolean;
    oba_category_expenditure: boolean;
  };
  valid_values?: ValidValuesUploadRecordApi[];
}

const stateConfigQueryKey = (stateCode: string) => ["admin", "state-config", stateCode] as const;

/** Query key for full current config (no state code in path — avoids 404 when state is renamed). */
export const stateConfigCurrentFullQueryKey = ["admin", "state-config", "current", "full"] as const;

async function fetchStateConfig(stateCode: string): Promise<StateConfigApiResponse> {
  const code = stateCode.trim().toUpperCase().slice(0, 2);
  const res = await authenticatedApi.get<StateConfigApiResponse>(`/admin/state-config/${code}`);
  const data =
    typeof res === "object" && "state_code" in res
      ? res
      : ((res as { data?: StateConfigApiResponse }).data ?? res);
  return data;
}

async function fetchStateConfigCurrentFull(): Promise<StateConfigApiResponse> {
  const res = await authenticatedApi.get<StateConfigApiResponse>(
    "/admin/state-config/current/full"
  );
  const data =
    typeof res === "object" && "state_code" in res
      ? res
      : ((res as { data?: StateConfigApiResponse }).data ?? res);
  return data;
}

export interface StateConfigUpdateResult extends StateConfigApiResponse {
  change_request_id?: string | null;
  request_number?: string | null;
}

async function updateStateConfigApi(
  stateCode: string,
  body: StateConfigUpdateApi
): Promise<StateConfigUpdateResult> {
  const code = stateCode.trim().toUpperCase().slice(0, 2);
  const res = await authenticatedApi.put<StateConfigApiResponse>(
    `/admin/state-config/${code}`,
    body
  );
  const data =
    typeof res === "object" && "state_code" in res
      ? res
      : ((res as { data?: StateConfigApiResponse }).data ?? res);
  return data;
}

async function updateStateConfigCurrentApi(
  body: StateConfigUpdateApi
): Promise<StateConfigUpdateResult> {
  const res = await authenticatedApi.put<StateConfigApiResponse>(
    "/admin/state-config/current",
    body
  );
  const data =
    typeof res === "object" && "state_code" in res
      ? res
      : ((res as { data?: StateConfigApiResponse }).data ?? res);
  return data;
}

export function useStateConfigQuery(stateCode: string, options?: { enabled?: boolean }) {
  const code = stateCode.trim().toUpperCase().slice(0, 2);
  return useQuery({
    queryKey: stateConfigQueryKey(code),
    queryFn: () => fetchStateConfig(code),
    enabled:
      options?.enabled !== undefined ? options.enabled && code.length === 2 : code.length === 2,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: (previousData) => previousData,
    retry: (failureCount, error) => {
      // Don't retry a 404 (resource not configured yet). Duck-type the status rather than
      // `instanceof ApiError` so this stays robust when a test mocks the API client without the
      // `ApiError` export (referencing it there throws an unhandled rejection inside the retryer).
      const status = (error as { status?: number } | null | undefined)?.status;
      if (status === 404) return false;
      return failureCount < 2;
    },
  });
}

/** Load full state config by "current" (no state code in URL). Use this for State Configuration page to avoid 404 when state is renamed. */
export function useStateConfigCurrentFullQuery() {
  return useQuery({
    queryKey: stateConfigCurrentFullQueryKey,
    queryFn: fetchStateConfigCurrentFull,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: (failureCount, error) => {
      // Don't retry a 404 (resource not configured yet). Duck-type the status rather than
      // `instanceof ApiError` so this stays robust when a test mocks the API client without the
      // `ApiError` export (referencing it there throws an unhandled rejection inside the retryer).
      const status = (error as { status?: number } | null | undefined)?.status;
      if (status === 404) return false;
      return failureCount < 2;
    },
  });
}

/** Save state config via PUT /current (no state code in URL). Use for State Configuration page so no stale path or 404. */
export function useStateConfigCurrentMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: StateConfigUpdateApi) => updateStateConfigCurrentApi(body),
    onSuccess: (data) => {
      queryClient.setQueryData(stateConfigCurrentFullQueryKey, data);
      void queryClient.invalidateQueries({ queryKey: stateConfigDefaultsQueryKey });
      void queryClient.invalidateQueries({ queryKey: BRANDING_QUERY_KEY });
      // The dashboard version tile + analytics thresholds re-serve config values under their own
      // keys — refresh them so a version/config change shows without a reload.
      invalidateConfigDerived(queryClient);
    },
  });
}

export function useStateConfigMutation(stateCode: string) {
  const queryClient = useQueryClient();
  const code = stateCode.trim().toUpperCase().slice(0, 2);
  return useMutation({
    mutationFn: (body: StateConfigUpdateApi) => updateStateConfigApi(code, body),
    onSuccess: (data) => {
      /* eslint-disable @typescript-eslint/no-confusing-void-expression -- fire-and-forget cache updates; ignorePromise returns void by design */
      if (data.state_code && data.state_code !== code) {
        ignorePromise(queryClient.removeQueries({ queryKey: stateConfigQueryKey(code) }));
        queryClient.setQueryData(stateConfigQueryKey(data.state_code), data);
      } else {
        ignorePromise(queryClient.invalidateQueries({ queryKey: stateConfigQueryKey(code) }));
      }
      ignorePromise(queryClient.invalidateQueries({ queryKey: stateConfigDefaultsQueryKey }));
      ignorePromise(queryClient.invalidateQueries({ queryKey: BRANDING_QUERY_KEY }));
      /* eslint-enable @typescript-eslint/no-confusing-void-expression */
      // Dashboard/analytics re-serve config values under their own keys — refresh them too.
      invalidateConfigDerived(queryClient);
    },
  });
}

/** Request body for valid values upload (VVL template rows). */
export interface ValidValuesUploadRecord {
  vvl_order: number;
  vvl_field: string;
  vvl_name: string;
  vvl_code: string;
  vvl_code_description: string | null;
  effective_start_date: string | null;
  effective_end_date: string | null;
}

export interface ValidValuesUploadResponse {
  saved: number;
  skipped: number;
}

/** Upload valid values (parsed from CSV/template) to the backend. Uses current state config. */
export async function uploadValidValuesApi(
  records: ValidValuesUploadRecord[]
): Promise<ValidValuesUploadResponse> {
  const res = await authenticatedApi.post<ValidValuesUploadResponse>(
    "/admin/state-config/current/valid-values/upload",
    { records }
  );
  return res;
}

/** Single valid value row from the backend. */
export interface ValidValueItem {
  id: string;
  code_set: string;
  valid_value_code: string;
  valid_value_name: string;
  description: string | null;
  effective_start_date: string | null;
  effective_end_date: string | null;
  is_active: boolean;
}

interface ValidValuesListResponse {
  items: ValidValueItem[];
  total: number;
  page: number;
  page_size: number;
}

export const validValuesQueryKey = ["admin", "valid-values"] as const;

/** Fetch persisted valid values from config.valid_values for the current state. */
export function useValidValues(page = 1, pageSize = 500) {
  return useQuery<ValidValuesListResponse>({
    queryKey: [...validValuesQueryKey, page, pageSize] as const,
    queryFn: () =>
      authenticatedApi.get<ValidValuesListResponse>("/admin/state-config/current/valid-values", {
        params: { page, page_size: pageSize },
      }),
    staleTime: 60 * 1000,
  });
}
